import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarClock,
  Inbox,
  Wallet,
  CheckCircle2,
  PoundSterling,
  ChevronRight,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import api from '@/lib/api';
import { formatGBP } from '@/lib/utils';
import PartnerStatusBanner from '@/components/partner/PartnerStatusBanner';

function MiniMetric({ label, value, icon: Icon, accent, testId }) {
  return (
    <Card className="p-3 border-slate-200" data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-slate-500 uppercase tracking-wider">{label}</p>
        <div className={`h-7 w-7 rounded-lg flex items-center justify-center ${accent}`}>
          <Icon className="h-3.5 w-3.5 text-white" />
        </div>
      </div>
      <p className="text-xl font-bold text-slate-900 mt-1">{value}</p>
    </Card>
  );
}

function QuickAction({ icon: Icon, label, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="w-full flex items-center justify-between p-3 rounded-xl bg-white border border-slate-200 hover:border-rose-300 hover:bg-rose-50/30 transition-colors"
    >
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg bg-slate-900 flex items-center justify-center">
          <Icon className="h-4 w-4 text-white" />
        </div>
        <span className="text-sm font-medium text-slate-900">{label}</span>
      </div>
      <ChevronRight className="h-4 w-4 text-slate-400" />
    </button>
  );
}

export default function PartnerDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.get('/partner/dashboard');
      setData(resp);
    } catch (e) {
      // Non-fatal: dashboard widget will show fallback content if data missing
      if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('Failed to load partner dashboard:', e?.message || e);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-4">
      <PartnerStatusBanner
        status={data?.partner_status || 'pending'}
        businessName={data?.business_name}
      />

      <div className="grid grid-cols-2 gap-3">
        <MiniMetric
          testId="metric-pending"
          label="Pending requests"
          value={data?.pending_requests ?? (loading ? '…' : 0)}
          icon={Inbox}
          accent="bg-amber-500"
        />
        <MiniMetric
          testId="metric-today"
          label="Today's bookings"
          value={data?.todays_bookings ?? (loading ? '…' : 0)}
          icon={CalendarClock}
          accent="bg-sky-600"
        />
        <MiniMetric
          testId="metric-completed"
          label="Completed jobs"
          value={data?.completed_jobs ?? (loading ? '…' : 0)}
          icon={CheckCircle2}
          accent="bg-emerald-600"
        />
        <MiniMetric
          testId="metric-earnings"
          label="Total earnings"
          value={formatGBP(data?.total_earnings)}
          icon={Wallet}
          accent="bg-rose-600"
        />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-slate-500">BeautyMateUK commission so far</p>
          <PoundSterling className="h-3.5 w-3.5 text-slate-400" />
        </div>
        <p className="text-lg font-bold text-rose-600">{formatGBP(data?.platform_commission)}</p>
        <p className="text-[11px] text-slate-400 mt-0.5">20% of service price on completed jobs</p>
      </div>

      <div className="space-y-2">
        <p className="text-xs text-slate-500 uppercase tracking-wider px-1">Quick actions</p>
        <QuickAction icon={Inbox} label="View Bookings" onClick={() => navigate('/partner/bookings')} testId="qa-bookings" />
        <QuickAction icon={Wallet} label="View Earnings" onClick={() => navigate('/partner/earnings')} testId="qa-earnings" />
        <QuickAction icon={CalendarClock} label="Manage Services" onClick={() => navigate('/partner/profile?tab=services')} testId="qa-services" />
        <QuickAction icon={CheckCircle2} label="Edit Profile" onClick={() => navigate('/partner/profile')} testId="qa-profile" />
      </div>
    </div>
  );
}
